import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SelectGroup, SelectOption } from "../control/types";
import { SelectProps } from "./select";
import { Options, OptionsOrGroups } from "./types";
import { cleanValue, setValue, valueTernary } from "./utils";

export const useSelect = <
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = boolean,
  Group extends SelectGroup<Option> = SelectGroup<Option>,
>(
  props: SelectProps<Option, IsMulti, Group>,
) => {
  const {
    value,
    defaultValue,
    options = [],
    isMulti,
    getInputRef,
    onChange,
    isOptionDisabled,
    emptyOption,
  } = props;

  const availableOptions = useMemo<OptionsOrGroups<Option, Group>>(
    () => (emptyOption ? [emptyOption, ...options] : options),
    [emptyOption, options],
  );

  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] =
    useState<OptionsOrGroups<Option, Group>>(availableOptions);
  const [selectedValue, setSelectedValue] = useState<Options<Option>>(
    cleanValue(value ?? defaultValue),
  );

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // onInputChange
  useEffect(() => {
    if (!inputValue) {
      // wait for menu closing transition
      const timeout = setTimeout(
        () => setFilteredOptions(availableOptions),
        100,
      );
      return () => clearTimeout(timeout);
    }

    const filtered = availableOptions
      .map((option) => {
        if ("options" in option) {
          const subOptions = option.options
            .map((sub) =>
              sub.label.toLowerCase().includes(inputValue.toLowerCase().trim())
                ? sub
                : null,
            )
            .filter((sub) => !!sub);
          return subOptions.length ? { ...option, options: subOptions } : null;
        }

        return option.label
          .toLowerCase()
          .includes(inputValue.toLowerCase().trim())
          ? option
          : null;
      })
      .filter((option) => !!option) as (Option | Group)[];

    setFilteredOptions(filtered);
  }, [availableOptions, inputValue]);

  useEffect(() => {
    setSelectedValue(cleanValue(value));
  }, [value]);

  const onSelect = useCallback(
    (key: string) => {
      if (key === emptyOption?.id) {
        if (value === undefined) setSelectedValue([]);
        onChange?.(valueTernary(isMulti, [], null));
        setInputValue("");
        inputRef.current?.focus();
        return;
      }

      const selected = availableOptions
        .flatMap((opt) => ("options" in opt ? opt.options : opt))
        .find((opt) => opt.id === key)!;

      const disabled = isOptionDisabled?.(selected, selectedValue);
      if (!isMulti && disabled) return;
      if (
        isMulti &&
        disabled &&
        !selectedValue.some((value) => value.id === key)
      )
        return;

      const newValue = setValue(selectedValue, selected, isMulti);

      onChange?.(valueTernary(isMulti, newValue, newValue[0]));
      if (value === undefined) setSelectedValue(newValue);
      setInputValue("");
      inputRef.current?.focus();
    },
    [
      availableOptions,
      emptyOption?.id,
      isMulti,
      isOptionDisabled,
      onChange,
      selectedValue,
      value,
    ],
  );

  const onClose = useCallback(() => setInputValue(""), []);

  const onClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (value === undefined) setSelectedValue(cleanValue([]));
      onChange?.(valueTernary(isMulti, [], null));
      inputRef.current?.focus();
    },
    [value, isMulti, onChange],
  );

  const onToggleClick = useCallback(() => inputRef.current?.focus(), []);

  const ref = useCallback(
    (ref: HTMLInputElement) => {
      inputRef.current = ref;
      getInputRef?.(ref);
    },
    [getInputRef],
  );

  return {
    inputValue,
    filteredOptions,
    selectedValue:
      selectedValue.length || !emptyOption ? selectedValue : [emptyOption],
    availableOptions,
    ref,
    setInputValue,
    onSelect,
    onClose,
    onClear,
    onToggleClick,
  };
};
